import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { colors } from '../../lib/theme';

export function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <section className="mt-16 mb-8">
      <div
        className="rounded-2xl px-6 py-10 text-center"
        style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
      >
        <h2 className="display text-[28px] tracking-tight" style={{ color: colors.textPrimary }}>
          Get Early Access
        </h2>
        <p className="mt-3 text-[15px] max-w-xl mx-auto leading-relaxed" style={{ color: colors.textSecondary }}>
          Be the first to know when EthicCheck launches. Join our waitlist for exclusive early access
          and updates.
        </p>

        <div className="max-w-md mx-auto mt-7">
          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Waitlist signup">
            <div className="flex gap-3">
              <input
                id="waitlist-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                aria-label="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-xl text-sm placeholder:text-slate-500 focus:outline-none"
                style={{
                  backgroundColor: colors.bgTertiary,
                  border: `1px solid ${colors.border}`,
                  color: colors.textPrimary,
                }}
                disabled={status === 'loading'}
              />
              <button
                type="submit"
                disabled={status === 'loading' || !email}
                aria-label="Join Waitlist"
                className="px-5 py-3 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: status === 'success' ? colors.green : colors.blue,
                  color: status === 'success' ? colors.ctaInk : '#06283d',
                  border: 'none',
                }}
              >
                {status === 'loading' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : status === 'success' ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  'Join Waitlist'
                )}
              </button>
            </div>

            {message && (
              <div
                className="text-sm p-3 rounded-xl"
                style={{
                  color: status === 'success' ? colors.green : colors.red,
                  backgroundColor: status === 'success' ? `${colors.green}20` : `${colors.red}20`,
                  border: `1px solid ${status === 'success' ? colors.green : colors.red}40`,
                }}
              >
                {message}
              </div>
            )}
          </form>

          <p className="text-xs mt-4" style={{ color: colors.textFaint }}>
            No spam, ever. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
}
